import { useState, useEffect } from 'react';
import { InAppNotification } from '@/types/intelligentScheduling';
import { smartNotificationService } from '@/services/intelligentScheduling/SmartNotificationService';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import {
  Bell,
  Clock,
  AlertCircle,
  Target,
  Calendar,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load initial notifications
    const loadNotifications = async () => {
      try {
        const activeNotifications = smartNotificationService.getActiveNotifications();
        setNotifications(activeNotifications);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();

    // Listen for notification updates
    const handleNotificationUpdate = (event: string, data: InAppNotification | { notificationId: string }) => {
      switch (event) {
        case 'notification_added':
          setNotifications(prev => [data as InAppNotification, ...prev]);
          break;
        case 'notification_updated':
          setNotifications(prev => 
            prev.map(n => n.id === (data as InAppNotification).id ? data as InAppNotification : n)
          );
          break;
        case 'notification_removed':
          setNotifications(prev => 
            prev.filter(n => n.id !== (data as { notificationId: string }).notificationId)
          );
          break;
      }
    };

    smartNotificationService.addNotificationListener(handleNotificationUpdate);

    return () => {
      smartNotificationService.removeNotificationListener(handleNotificationUpdate);
    };
  }, []);

  const handleNotificationAction = async (notificationId: string, action: string, data?: any) => {
    try {
      await smartNotificationService.handleNotificationAction(notificationId, action, data);
    } catch (error) {
      console.error('Failed to handle notification action:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Smart Notifications"
      size="md"
      animation="slide"
    >
      <div className="max-h-96 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-2 border-b">
          <div className="flex items-center space-x-2">
            <Bell className="h-5 w-5 text-primary" />
            <span className="font-medium">Notifications</span>
            {unreadCount > 0 && (
              <Badge variant="default" className="text-xs">
                {unreadCount} new
              </Badge>
            )}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading notifications...</div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground font-medium">No notifications yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              We'll notify you when it's time for your scheduled tasks
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onAction={(action, data) => handleNotificationAction(notification.id, action, data)}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

interface NotificationItemProps {
  notification: InAppNotification;
  onAction: (action: string, data?: any) => void;
}

function NotificationItem({ notification, onAction }: NotificationItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const getIconComponent = () => {
    switch (notification.iconType) {
      case 'clock': return <Clock className="h-4 w-4" />;
      case 'alert': return <AlertCircle className="h-4 w-4" />;
      case 'target': return <Target className="h-4 w-4" />;
      case 'calendar': return <Calendar className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getPriorityStyles = () => {
    switch (notification.priority) {
      case 'critical': return 'border-red-200 bg-red-50 text-red-800';
      case 'high': return 'border-orange-200 bg-orange-50 text-orange-800';
      case 'medium': return 'border-blue-200 bg-blue-50 text-blue-800';
      default: return 'border-gray-200 text-gray-800';
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  return (
    <div className={`border rounded-lg p-4 transition-all duration-200 hover:shadow-sm ${
      !notification.isRead ? 'ring-2 ring-primary/20 bg-primary/5' : 'bg-background'
    }`}>
      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className={`p-2 rounded-full flex-shrink-0 ${getPriorityStyles()}`}>
          {getIconComponent()}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-sm font-medium truncate ${
              !notification.isRead ? 'text-foreground' : 'text-muted-foreground'
            }`}>
              {notification.title}
            </h3>
            <div className="flex items-center space-x-2 ml-2">
              {notification.confidence && (
                <Badge variant="outline" className="text-xs">
                  {Math.round(notification.confidence * 100)}%
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </Button>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
            {notification.message}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center text-xs text-muted-foreground">
              <Clock className="h-3 w-3 mr-1" />
              {formatTimeAgo(notification.triggerTime)}
            </div>
            
            {/* Priority indicator */}
            <Badge 
              variant={notification.priority === 'critical' || notification.priority === 'high' ? 'destructive' : 'secondary'}
              className="text-xs"
            >
              {notification.priority}
            </Badge>
          </div>

          {/* Expanded content */}
          {isExpanded && (
            <div className="mt-3 space-y-3 border-t pt-3">
              {/* Contextual factors */}
              {notification.contextualFactors.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Why now?</p>
                  <div className="flex flex-wrap gap-1">
                    {notification.contextualFactors.map(factor => (
                      <Badge key={factor} variant="outline" className="text-xs">
                        {factor.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2">
                {notification.actions.slice(0, 4).map(action => (
                  <Button
                    key={action.id}
                    size="sm"
                    variant={action.action === 'complete' ? 'primary' : 'outline'}
                    onClick={() => onAction(action.action, action.data)}
                    className="text-xs h-8"
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}